
/**
 * Basic cookie operations that handle setting, getting, and removing cookies
 * with proper error handling and browser compatibility
 */

/**
 * Sets a cookie with the given name, value and expiration days
 * Optimized to avoid CSP issues by using direct string manipulation
 */
export const setCookie = (name: string, value: string, days: number = 365) => {
  try {
    if (!document || typeof document.cookie !== 'string') {
      console.log('Document or cookie API not available');
      return false;
    }
    
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "; expires=" + date.toUTCString();
    
    // Use direct string assignment to avoid potential CSP issues
    document.cookie = name + "=" + encodeURIComponent(value || "") + 
                     expires + 
                     "; path=/; SameSite=Lax";
    
    // Verify cookie was set successfully
    const success = document.cookie.indexOf(name + "=") >= 0;
    
    if (!success) {
      console.log(`Failed to set cookie ${name} - cookies may be blocked`);
    }
    
    return success;
  } catch (error) {
    console.error(`Failed to set cookie ${name}:`, error);
    return false;
  }
};

/**
 * Gets a cookie value by name
 * @returns The cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  try {
    if (!document || typeof document.cookie !== 'string') {
      return null;
    }
    
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    // Simple loop to find the cookie - no eval or Function constructor needed
    for(let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        // Decode the cookie value
        return decodeURIComponent(c.substring(nameEQ.length));
      }
    }
    return null;
  } catch (error) {
    console.error(`Failed to get cookie ${name}:`, error);
    return null;
  }
};

/**
 * Removes a cookie by name
 */
export const removeCookie = (name: string) => {
  try {
    if (!document || typeof document.cookie !== 'string') {
      return false;
    }
    
    // Set cookie to empty with expired date to remove it
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax';
    
    // Verify cookie was removed
    const success = document.cookie.indexOf(name + "=") < 0;
    return success;
  } catch (error) {
    console.error(`Failed to remove cookie ${name}:`, error);
    return false;
  }
};
